<template>
    <div class="manage-vendors">

        <div v-if="messageType === 'vendor_flow'"
             class="mv-text-message mv-phone-card">

            <div class="mv-phone-question"
                 v-html="formatPrompt(responseText)">
            </div>

            <div class="mv-phone-row">
                <Dropdown v-model="phoneForm.countryCode"
                          :options="countryCodeList"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          :disabled="disabled"
                          appendTo="self"
                          class="mv-country-dropdown"
                          @change="onVendorCountryCodeChange" />

                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="mv-phone-input"
                       :disabled="disabled"
                       :maxlength="phoneLength === 10 ? phoneLength + 4 : phoneLength"
                       placeholder="Phone number"
                       @input="onVendorPhoneInput" />
            </div>

            <div v-if="phoneError" class="mv-phone-error">
                {{ phoneError }}
            </div>

            <button type="button"
                    class="mv-btn mv-btn--primary mv-phone-submit"
                    :disabled="disabled"
                    @click="submitVendorPhone">
                Continue
            </button>
        </div>

        <div v-else-if="messageType === 'vendor_preview'"
             class="mv-summary-card">

            <div class="mv-summary-header">
                <div>
                    <div class="mv-header-title">Vendor Summary</div>
                    <div class="mv-header-subtitle">
                        {{ cleanResponseText || 'Please review before registering.' }}
                    </div>
                </div>
            </div>

            <div class="mv-summary-body">
                <div class="mv-row">
                    <span class="mv-label">Vendor Name</span>
                    <span class="mv-value">{{ vendorForm.vendorName || '—' }}</span>
                </div>

                <label class="mv-edit-row">
                    <span class="mv-label">Company Name</span>
                    <input v-model.trim="vendorForm.companyName"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <div class="mv-row">
                    <span class="mv-label">Email</span>
                    <span class="mv-value mv-value--wrap">{{ vendorForm.email || '—' }}</span>
                </div>

                <div class="mv-edit-row">
                    <span class="mv-label">Mobile</span>
                    <div class="mv-preview-phone-row">
                        <Dropdown v-model="vendorForm.countryCodeId"
                                  :options="previewCountryCodes"
                                  optionValue="value"
                                  optionLabel="text"
                                  placeholder="Code"
                                  filter
                                  :disabled="disabled"
                                  class="mv-preview-country-dropdown"
                                  @change="onPreviewCountryCodeChange" />

                        <input v-model="vendorForm.phone"
                               type="text"
                               inputmode="numeric"
                               class="mv-input mv-preview-phone-input"
                               :disabled="disabled"
                               :maxlength="previewPhoneLength === 10 ? previewPhoneLength + 4 : previewPhoneLength"
                               placeholder="Phone number"
                               @input="onPreviewPhoneInput" />
                    </div>
                </div>

                <div class="mv-row">
                    <span class="mv-label">Assign Place</span>
                    <span class="mv-value">{{ vendorForm.assetName || '—' }}</span>
                </div>

                <div class="mv-edit-row mv-edit-row--top">
                    <span class="mv-label">Work Type</span>
                    <div class="mv-multi" ref="workTypeDropdown">
                        <button type="button"
                                class="mv-multi__trigger"
                                :disabled="disabled"
                                @click="toggleDropdown('workType')">
                            <span>{{ workTypeDropdownLabel }}</span>
                            <span class="mv-chevron">{{ workTypeOpen ? '▲' : '▼' }}</span>
                        </button>

                        <div v-if="workTypeOpen" class="mv-multi__panel">
                            <label class="mv-check mv-check--all">
                                <input type="checkbox"
                                       :checked="allWorkTypesSelected"
                                       :disabled="disabled || !workTypeOptions.length"
                                       @change="toggleAllWorkTypes($event.target.checked)" />
                                <span>Select All</span>
                            </label>

                            <label v-for="item in workTypeOptions"
                                   :key="item.value"
                                   class="mv-check">
                                <input v-model="selectedWorkTypeIds"
                                       type="checkbox"
                                       :value="String(item.value)"
                                       :disabled="disabled"
                                       @change="onWorkTypeChange" />
                                <span>{{ item.text }}</span>
                            </label>

                            <div v-if="!workTypeOptions.length"
                                 class="mv-empty-inline">
                                No work types available.
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mv-edit-row mv-edit-row--top">
                    <span class="mv-label">Service</span>
                    <div class="mv-multi" ref="serviceDropdown">
                        <button type="button"
                                class="mv-multi__trigger"
                                :disabled="disabled"
                                @click="toggleDropdown('service')">
                            <span>{{ serviceDropdownLabel }}</span>
                            <span class="mv-chevron">{{ serviceOpen ? '▲' : '▼' }}</span>
                        </button>

                        <div v-if="serviceOpen" class="mv-multi__panel">
                            <label class="mv-check mv-check--all">
                                <input type="checkbox"
                                       :checked="allServicesSelected"
                                       :disabled="disabled || !serviceOptions.length"
                                       @change="toggleAllServices($event.target.checked)" />
                                <span>Select All</span>
                            </label>

                            <label v-for="item in serviceOptions"
                                   :key="item.value"
                                   class="mv-check">
                                <input v-model="selectedServiceIds"
                                       type="checkbox"
                                       :value="String(item.value)"
                                       :disabled="disabled" />
                                <span>{{ item.text }}</span>
                            </label>

                            <div v-if="!serviceOptions.length"
                                 class="mv-empty-inline">
                                No services available.
                            </div>
                        </div>
                    </div>
                </div>

                <label class="mv-edit-row">
                    <span class="mv-label">State</span>
                    <Dropdown v-model="vendorForm.stateId"
                              :options="stateOptions"
                              optionValue="value"
                              optionLabel="text"
                              placeholder="Select..."
                              filter
                              showClear
                              :disabled="disabled"
                              class="mv-form-dropdown" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">Country</span>
                    <Dropdown v-model="vendorForm.countryId"
                              :options="countryOptions"
                              optionValue="value"
                              optionLabel="text"
                              placeholder="Select..."
                              filter
                              showClear
                              :disabled="disabled"
                              class="mv-form-dropdown" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">Street Address</span>
                    <input v-model.trim="vendorForm.address"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">City</span>
                    <input v-model.trim="vendorForm.city"
                           type="text"
                           class="mv-input"
                           :disabled="disabled" />
                </label>

                <label class="mv-edit-row">
                    <span class="mv-label">Zip</span>
                    <input v-model.trim="vendorForm.zipCode"
                           type="text"
                           class="mv-input"
                           maxlength="10"
                           :disabled="disabled" />
                </label>

                <label class="mv-field">
                    <span class="mv-label">Description</span>
                    <textarea v-model.trim="vendorForm.description"
                              rows="3"
                              class="mv-textarea"
                              :disabled="disabled"></textarea>
                </label>

                <p v-if="formError" class="mv-error">{{ formError }}</p>
            </div>

            <div class="mv-actions mv-actions--stacked">
                <button type="button"
                        class="mv-btn mv-btn--primary"
                        :disabled="disabled"
                        @click="confirmVendor">
                    Confirm &amp; Register
                </button>

                <button type="button"
                        class="mv-btn mv-btn--cancel"
                        :disabled="disabled"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>

        <div v-else-if="messageType === 'vendor_created' || messageType === 'vendor_failed'"
             class="ticket-preview-card">

            <div class="ticket-preview-header"
                 :class="{ 'mv-error-header': isError }">
                <div>
                    <h4>{{ isError ? 'Vendor Not Added' : 'Vendor Added!' }}</h4>
                    <p>{{ isError ? 'Something went wrong.' : 'The vendor has been registered.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-body">
                <div class="ticket-preview-row"><span class="tp-label">Vendor Name</span><span class="tp-value">{{ vendorData.vendorName || '—' }}</span></div>
                <div class="ticket-preview-row"><span class="tp-label">Company</span><span class="tp-value">{{ vendorData.companyName || '—' }}</span></div>
                <div class="ticket-preview-row"><span class="tp-label">Email</span><span class="tp-value">{{ vendorData.email || '—' }}</span></div>
                <div class="ticket-preview-row"><span class="tp-label">Assign Place</span><span class="tp-value">{{ vendorData.assetName || '—' }}</span></div>
                <div class="ticket-preview-row"><span class="tp-label">Work Type</span><span class="tp-value">{{ vendorData.workTypeCategoryName || vendorData.workType || '—' }}</span></div>
                <div class="ticket-preview-row"><span class="tp-label">Service</span><span class="tp-value">{{ vendorData.serviceName || vendorData.serviceTitle || '—' }}</span></div>

                <div v-if="isError" class="ticket-preview-error">
                    <p><strong>Error:</strong> {{ vendorData.error || 'Please try again.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-footer">
                <span>{{ formatMMDDYYYY(createdDate) }}</span>
                <span :class="{ 'mv-error-text': isError, 'mv-saved': !isError }">
                    {{ isError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </div>

        <div v-else-if="messageType === 'vendor_list'"
             class="ticket-preview-card">

            <div class="ticket-preview-header">
                <div>
                    <h4>{{ vendorData.heading || 'Available Vendors' }}</h4>
                    <p>{{ vendors.length }} vendor{{ vendors.length === 1 ? '' : 's' }} found</p>
                </div>
                <span v-if="vendors.length" class="ticket-preview-number">{{ vendors.length }}</span>
            </div>

            <div class="ticket-preview-body mv-list-body">
                <div v-if="!vendors.length" class="mv-empty">No vendors found.</div>

                <div v-for="(vendor, index) in vendors"
                     :key="vendor.vendorId || vendor.id || index"
                     class="mv-list-item"
                     :class="{ 'mv-list-item--last': index === vendors.length - 1 }">

                    <div class="mv-list-top">
                        <div class="mv-list-main">
                            <div class="mv-list-title">{{ vendor.vendorName || 'Vendor' }}</div>
                            <div class="mv-list-meta">{{ vendor.email || '—' }}</div>
                        </div>

                        <div class="mv-list-actions">
                            <button type="button"
                                    class="mv-detail-btn"
                                    :disabled="disabled"
                                    @click="toggleVendorDetails(vendor, index)">
                                {{ isVendorDetailsOpen(vendor, index) ? 'Hide Details' : 'Details' }}
                            </button>

                            <button type="button"
                                    class="mv-status-btn"
                                    :class="vendor.isActive ? 'mv-status-btn--active' : 'mv-status-btn--inactive'"
                                    :disabled="disabled"
                                    @click="requestToggleStatus(vendor)">
                                {{ vendor.isActive ? 'Active' : 'Inactive' }}
                            </button>
                        </div>
                    </div>

                    <div v-if="isVendorDetailsOpen(vendor, index)"
                         class="mv-vendor-details">
                        <div class="mv-detail-row"><span>Work Type</span><strong>{{ vendor.workType || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Email</span><strong>{{ vendor.email || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Phone</span><strong>{{ vendor.phone || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Service</span><strong>{{ vendor.serviceTitle || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Company</span><strong>{{ vendor.companyName || '—' }}</strong></div>
                        <div class="mv-detail-row"><span>Created</span><strong>{{ formatMMDDYYYY(vendor.createdOn) }}</strong></div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="textOnlyTypes.includes(messageType)"
             class="mv-text-message">
            {{ cleanResponseText }}
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';

    function formatMMDDYYYY(value) {
        if (!value) return '—';

        const text = String(value).trim();
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;

        const date = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;

        return text;
    }

    // Keep the country code selected in the phone step so the Vendor Summary
    // shows the same selection instead of falling back to US.
    let lastVendorCountryCodeId = '';
    let lastVendorCountryCodeText = '';

    const blankVendorForm = () => ({
        vendorName: '',
        companyName: '',
        email: '',
        countryCodeId: '',
        countryCodeText: '',
        phone: '',
        assetId: '',
        assetName: '',
        workTypeCategoryId: '',
        workTypeCategoryName: '',
        serviceId: '',
        serviceName: '',
        stateId: '',
        stateName: '',
        countryId: '',
        countryName: '',
        address: '',
        city: '',
        zipCode: '',
        description: '',
    });

    export default {
        name: 'ManageVendors',

        components: { Dropdown },

        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },

        emits: ['confirm-vendor', 'cancel', 'toggle-status', 'phone-submit'],

        data() {
            return {
                vendorForm: blankVendorForm(),
                selectedWorkTypeIds: [],
                selectedServiceIds: [],
                loadedServices: [],
                workTypeOpen: false,
                serviceOpen: false,
                countryCodeList: [],
                phoneForm: { countryCode: '', phoneNumber: '' },
                phoneLength: 10,
                phoneError: '',
                previewPhoneLength: 10,
                formError: '',
                expandedVendorKey: '',
                textOnlyTypes: [
                    'vendor_cancelled',
                    'vendor_list_failed',
                    'vendor_status_updated',
                    'vendor_status_update_failed',
                ],
            };
        },

        async created() {
            await this.GetCountryCodeList();
        },

        mounted() {
            document.addEventListener('click', this.handleDropdownOutsideClick);
        },

        beforeUnmount() {
            document.removeEventListener('click', this.handleDropdownOutsideClick);
        },

        computed: {
            payload() {
                return this.msg && this.msg.payload ? this.msg.payload : {};
            },

            vendorData() {
                return this.payload.vendor_data || {};
            },

            prefilled() {
                return this.vendorData.prefilled || this.vendorData || {};
            },

            vendors() {
                return Array.isArray(this.vendorData.vendors) ? this.vendorData.vendors : [];
            },

            messageType() {
                return this.msg?.type || this.msg?.Intent || '';
            },

            responseText() {
                return this.payload.response
                    || this.payload.formal_response
                    || this.msg?.response
                    || this.msg?.formal_response
                    || this.msg?.text
                    || '';
            },

            cleanResponseText() {
                return String(this.responseText || '').replace(/\*/g, '').trim();
            },
            isVendorPhonePrompt() {
                if (this.messageType !== 'vendor_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes('mobile number')
                    || text.includes('mobile')
                    || text.includes('contact number')
                    || text.includes('contact no')
                    || text.includes('phone');
            },

            isError() {
                return this.messageType === 'vendor_failed';
            },

            createdDate() {
                return this.vendorData.createdOn
                    || this.vendorData.createdDate
                    || this.vendorData.createdAt
                    || this.currentDate
                    || '';
            },

            previewCountryCodes() {
                const fromPayload = this.vendorData.countryCodes || [];

                if (Array.isArray(fromPayload) && fromPayload.length) {
                    return fromPayload.map(item => ({
                        value: item.value ?? item.id,
                        text: item.text ?? item.name,
                    }));
                }

                return this.countryCodeList;
            },

            stateOptions() {
                return (Array.isArray(this.vendorData.states) ? this.vendorData.states : []).map(item => ({
                    value: item.value ?? item.id,
                    text: item.text ?? item.name,
                }));
            },

            countryOptions() {
                return (Array.isArray(this.vendorData.countries) ? this.vendorData.countries : []).map(item => ({
                    value: item.value ?? item.id,
                    text: item.text ?? item.name,
                }));
            },

            workTypeOptions() {
                return (Array.isArray(this.vendorData.workTypeCategories) ? this.vendorData.workTypeCategories : []).map(item => ({
                    value: String(item.id ?? item.value ?? ''),
                    text: item.workTypeCategory ?? item.text ?? item.name ?? '',
                })).filter(item => item.value && item.text);
            },

            serviceOptions() {
                const source = this.loadedServices.length
                    ? this.loadedServices
                    : (Array.isArray(this.vendorData.services) ? this.vendorData.services : []);

                return source.map(item => ({
                    value: String(
                        item.id ??
                        item.Id ??
                        item.value ??
                        item.serviceId ??
                        item.ServiceId ??
                        ''
                    ),
                    text:
                        item.title ??
                        item.Title ??
                        item.text ??
                        item.name ??
                        item.serviceName ??
                        item.ServiceName ??
                        ''
                })).filter(item => item.value && item.text);
            },

            allWorkTypesSelected() {
                return this.workTypeOptions.length > 0
                    && this.workTypeOptions.every(item => this.selectedWorkTypeIds.includes(String(item.value)));
            },

            allServicesSelected() {
                return this.serviceOptions.length > 0
                    && this.serviceOptions.every(item => this.selectedServiceIds.includes(String(item.value)));
            },

            workTypeDropdownLabel() {
                if (!this.selectedWorkTypeIds.length) return 'Select work type...';

                if (this.selectedWorkTypeIds.length === 1) {
                    const selected = this.workTypeOptions.find(item =>
                        this.selectedWorkTypeIds.includes(String(item.value))
                    );
                    return selected ? selected.text : '1 selected';
                }

                return `${this.selectedWorkTypeIds.length} work types selected`;
            },

            serviceDropdownLabel() {
                if (!this.selectedServiceIds.length) return 'Select service...';

                if (this.selectedServiceIds.length === 1) {
                    const selected = this.serviceOptions.find(item =>
                        this.selectedServiceIds.includes(String(item.value))
                    );
                    return selected ? selected.text : '1 selected';
                }

                return `${this.selectedServiceIds.length} services selected`;
            },
        },

        watch: {
            msg: {
                immediate: true,
                deep: true,
                handler() {
                    this.initializeFromMessage();
                },
            },
        },

        methods: {

            handleDropdownOutsideClick(event) {
                const workTypeDropdown = this.$refs.workTypeDropdown;
                const serviceDropdown = this.$refs.serviceDropdown;

                const clickedInsideWorkType =
                    workTypeDropdown &&
                    workTypeDropdown.contains(event.target);

                const clickedInsideService =
                    serviceDropdown &&
                    serviceDropdown.contains(event.target);

                if (!clickedInsideWorkType && !clickedInsideService) {
                    this.workTypeOpen = false;
                    this.serviceOpen = false;
                }
            },

            formatMMDDYYYY,

            formatPrompt(text) {
                const value = String(text || '');

                // Handle normal **bold** first, then remove any unmatched
                // markdown asterisks returned by the chatbot.
                return value
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*/g, '');
            },

            toIdArray(value) {
                if (Array.isArray(value)) return value.map(item => String(item)).filter(Boolean);

                return String(value || '')
                    .split(',')
                    .map(item => item.trim())
                    .filter(Boolean);
            },
            async loadVendorServices() {
                this.loadedServices = [];
                this.selectedServiceIds = [];
                this.formError = '';

                if (!this.selectedWorkTypeIds.length) {
                    this.serviceOpen = false;
                    return;
                }

                const companyId =
                    localStorage.getItem('companyId') ||
                    this.vendorData.companyId ||
                    this.vendorData.CompanyId ||
                    '';

                if (!companyId) {
                    this.formError = 'Company Id is required to load services.';
                    this.serviceOpen = false;
                    return;
                }

                const workTypeIds = this.selectedWorkTypeIds
                    .map(item => String(item || '').trim())
                    .filter(Boolean)
                    .join(',');

                if (!workTypeIds) {
                    this.serviceOpen = false;
                    return;
                }

                try {
                    const response = await UserService.getVendorServicesList({
                        CompanyId: companyId,
                        WorkTypeIds: workTypeIds,
                    });

                    const result = response?.data;

                    const rows = Array.isArray(result)
                        ? result
                        : Array.isArray(result?.data)
                            ? result.data
                            : Array.isArray(result?.result)
                                ? result.result
                                : [];

                    this.loadedServices = rows
                        .map(item => ({
                            id:
                                item.id ??
                                item.Id ??
                                item.value ??
                                item.serviceId ??
                                item.ServiceId ??
                                '',
                            title:
                                item.title ??
                                item.Title ??
                                item.text ??
                                item.name ??
                                item.serviceName ??
                                item.ServiceName ??
                                '',
                            workTypeId:
                                item.workTypeId ??
                                item.WorkTypeId ??
                                null,
                        }))
                        .filter(item =>
                            String(item.id || '').trim() &&
                            String(item.title || '').trim()
                        );

                    this.workTypeOpen = false;

                    await this.$nextTick();

                    // Open Service list automatically after Work Type selection.
                    this.serviceOpen = true;

                } catch (error) {
                    console.error(
                        'GetVendorServicesList error:',
                        error?.response || error
                    );

                    this.loadedServices = [];
                    this.serviceOpen = true;
                    this.formError =
                        error?.response?.data?.responseMessage ||
                        error?.response?.data?.message ||
                        (
                            error?.response?.status
                                ? `Unable to load services (${error.response.status}).`
                                : 'Unable to load services.'
                        );
                }
            },

            async onWorkTypeChange() {
                await this.loadVendorServices();
            },

            initializeFromMessage() {
                if (this.messageType !== 'vendor_preview') return;

                const p = this.prefilled || {};

                this.vendorForm = {
                    ...blankVendorForm(),
                    ...p,
                    vendorName: p.vendorName || '',
                    companyName: p.companyName || '',
                    email: p.email || '',
                    countryCodeId:
                        localStorage.getItem('vendorSelectedCountryCodeId') ||
                        lastVendorCountryCodeId ||
                        p.countryCodeId ||
                        '',
                    countryCodeText:
                        localStorage.getItem('vendorSelectedCountryCodeText') ||
                        lastVendorCountryCodeText ||
                        p.countryCodeText ||
                        '',
                    phone: p.phone || '',
                    assetId: p.assetId || p.assetID || '',
                    assetName: p.assetName || '',
                    workTypeCategoryId: p.workTypeCategoryId || '',
                    workTypeCategoryName: p.workTypeCategoryName || '',
                    serviceId: p.serviceId || '',
                    serviceName: p.serviceName || '',
                    stateId: p.stateId || '',
                    stateName: p.stateName || '',
                    countryId: p.countryId || '',
                    countryName: p.countryName || '',
                    address: p.address || '',
                    city: p.city || '',
                    zipCode: p.zipCode || '',
                    description: p.description || '',
                };

                this.selectedWorkTypeIds = this.toIdArray(p.workTypeCategoryId);
                this.selectedServiceIds = this.toIdArray(p.serviceId);
                this.loadedServices = Array.isArray(this.vendorData.services)
                    ? [...this.vendorData.services]
                    : [];
                this.workTypeOpen = false;
                this.serviceOpen = false;
                this.formError = '';

                this.previewPhoneLength =
                    resolvePhoneLength(this.vendorForm.countryCodeId, this.previewCountryCodes) || 10;
            },

            toggleDropdown(type) {
                if (this.disabled) return;

                if (type === 'workType') {
                    this.workTypeOpen = !this.workTypeOpen;
                    this.serviceOpen = false;
                } else {
                    this.serviceOpen = !this.serviceOpen;
                    this.workTypeOpen = false;
                }
            },

            async toggleAllWorkTypes(checked) {
                this.selectedWorkTypeIds = checked
                    ? this.workTypeOptions.map(item =>
                        String(item.value)
                    )
                    : [];

                this.selectedServiceIds = [];
                this.loadedServices = [];
                this.formError = '';

                if (!checked || !this.selectedWorkTypeIds.length) {
                    this.workTypeOpen = false;
                    this.serviceOpen = false;
                    return;
                }

                await this.loadVendorServices();
            },

            toggleAllServices(checked) {
                this.selectedServiceIds = checked
                    ? this.serviceOptions.map(item => String(item.value))
                    : [];
            },

            selectedNames(ids, options) {
                return ids.map(id => {
                    const item = options.find(option => String(option.value) === String(id));
                    return item ? item.text : '';
                }).filter(Boolean).join(', ');
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        if (!this.phoneForm.countryCode) {
                            this.phoneForm.countryCode = us.value;
                        }

                        this.phoneLength =
                            resolvePhoneLength(
                                this.phoneForm.countryCode,
                                this.countryCodeList
                            ) || 10;

                        if (
                            this.messageType === 'vendor_preview' &&
                            !this.vendorForm.countryCodeId
                        ) {
                            const rememberedCode =
                                localStorage.getItem('vendorSelectedCountryCodeId') ||
                                lastVendorCountryCodeId ||
                                us.value;

                            const rememberedText =
                                localStorage.getItem('vendorSelectedCountryCodeText') ||
                                lastVendorCountryCodeText ||
                                '';

                            this.vendorForm.countryCodeId = rememberedCode;
                            this.vendorForm.countryCodeText =
                                rememberedText ||
                                this.previewCountryCodes.find(
                                    item => String(item.value) === String(rememberedCode)
                                )?.text ||
                                '';
                            this.previewPhoneLength =
                                resolvePhoneLength(
                                    rememberedCode,
                                    this.previewCountryCodes
                                ) || 10;
                        }
                    }
                } catch (e) {
                    console.error('Vendor country code load failed', e);
                    this.countryCodeList = [];
                }
            },

            onVendorCountryCodeChange() {
                this.phoneLength =
                    resolvePhoneLength(this.phoneForm.countryCode, this.countryCodeList) || 10;

                const selectedCountryCode = (this.countryCodeList || []).find(
                    item => String(item.value) === String(this.phoneForm.countryCode)
                );

                lastVendorCountryCodeId = this.phoneForm.countryCode || '';
                lastVendorCountryCodeText = selectedCountryCode?.text || '';

                localStorage.setItem(
                    'vendorSelectedCountryCodeId',
                    lastVendorCountryCodeId
                );
                localStorage.setItem(
                    'vendorSelectedCountryCodeText',
                    lastVendorCountryCodeText
                );

                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
            },

            onVendorPhoneInput(event) {
                const maxLength = this.phoneLength || 10;

                const digits = String(event.target.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                let formatted = digits;

                if (maxLength === 10) {
                    if (digits.length > 6) {
                        formatted =
                            `(${digits.slice(0, 3)}) ` +
                            `${digits.slice(3, 6)}-${digits.slice(6)}`;
                    } else if (digits.length > 3) {
                        formatted =
                            `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                    }
                }

                this.phoneForm.phoneNumber = formatted;
                this.phoneError = '';
            },

            onPreviewCountryCodeChange() {
                this.previewPhoneLength =
                    resolvePhoneLength(this.vendorForm.countryCodeId, this.previewCountryCodes) || 10;

                this.vendorForm.phone = '';
            },

            onPreviewPhoneInput(event) {
                const maxLength = this.previewPhoneLength || 10;
                const digits = String(event?.target?.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                this.vendorForm.phone = this.formatPhoneDigits(digits, maxLength);
            },

            formatPhoneDigits(digits, maxLength) {
                if (maxLength !== 10) return digits;
                if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                if (digits.length > 0) return `(${digits}`;
                return '';
            },

            resolveVendorDialCode() {
                const value = this.phoneForm.countryCode;

                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => String(item.value) === String(value));

                if (!selected || !selected.text) {
                    return value;
                }

                const dialCode =
                    String(selected.text).trim().split(/\s+/)[0];

                return dialCode || value;
            },

            submitVendorPhone() {
                this.phoneError = '';

                if (!this.phoneForm.countryCode) {
                    this.phoneError =
                        'Please select a country code.';
                    return;
                }

                const digits =
                    String(this.phoneForm.phoneNumber || '')
                        .replace(/\D/g, '');

                const requiredLength =
                    this.phoneLength || 10;

                if (digits.length !== requiredLength) {
                    this.phoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode =
                    this.resolveVendorDialCode();

                let formattedPhone = digits;

                if (requiredLength === 10) {
                    formattedPhone =
                        `(${digits.slice(0, 3)}) ` +
                        `${digits.slice(3, 6)}-${digits.slice(6)}`;
                }

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                const selectedCountryCode = (this.countryCodeList || [])
                    .find(item =>
                        String(item.value) === String(this.phoneForm.countryCode)
                    );

                lastVendorCountryCodeId = this.phoneForm.countryCode || '';
                lastVendorCountryCodeText = selectedCountryCode?.text || '';

                localStorage.setItem(
                    'vendorSelectedCountryCodeId',
                    lastVendorCountryCodeId
                );
                localStorage.setItem(
                    'vendorSelectedCountryCodeText',
                    lastVendorCountryCodeText
                );

                this.$emit(
                    'phone-submit',
                    fullPhoneNumber
                );
            },

            confirmVendor() {
                this.formError = '';

                if (!this.selectedWorkTypeIds.length) {
                    this.formError = 'Please select at least one work type.';
                    return;
                }

                if (!this.selectedServiceIds.length) {
                    this.formError = 'Please select at least one service.';
                    return;
                }

                const countryCode = this.previewCountryCodes.find(item =>
                    String(item.value) === String(this.vendorForm.countryCodeId)
                );
                const state = this.stateOptions.find(item =>
                    String(item.value) === String(this.vendorForm.stateId)
                );
                const country = this.countryOptions.find(item =>
                    String(item.value) === String(this.vendorForm.countryId)
                );

                const selectedCountryCodeId =
                    localStorage.getItem('vendorSelectedCountryCodeId') ||
                    this.vendorForm.countryCodeId ||
                    '';

                const selectedCountryCodeText =
                    localStorage.getItem('vendorSelectedCountryCodeText') ||
                    countryCode?.text ||
                    this.vendorForm.countryCodeText ||
                    '';

                this.$emit('confirm-vendor', {
                    ...this.prefilled,
                    ...this.vendorForm,
                    countryCodeId: selectedCountryCodeId,
                    countryCodeText: selectedCountryCodeText,
                    stateName: state?.text || this.vendorForm.stateName || '',
                    countryName: country?.text || this.vendorForm.countryName || '',
                    workTypeCategoryId: this.selectedWorkTypeIds.join(','),
                    workTypeCategoryName: this.selectedNames(this.selectedWorkTypeIds, this.workTypeOptions),
                    serviceId: this.selectedServiceIds.join(','),
                    serviceName: this.selectedNames(this.selectedServiceIds, this.serviceOptions),
                });
            },

            vendorKey(vendor, index) {
                return String(vendor.vendorId || vendor.id || index);
            },

            toggleVendorDetails(vendor, index) {
                const key = this.vendorKey(vendor, index);
                this.expandedVendorKey = this.expandedVendorKey === key ? '' : key;
            },

            isVendorDetailsOpen(vendor, index) {
                return this.expandedVendorKey === this.vendorKey(vendor, index);
            },

            requestToggleStatus(vendor) {
                if (!vendor) return;

                this.$emit('toggle-status', {
                    vendorId: vendor.vendorId || vendor.id || '',
                    vendorName: vendor.vendorName || 'Vendor',
                    isActive: vendor.isActive !== true,
                });
            },
        },
    };</script>

<style scoped>
    .manage-vendors {
        width: 100%;
        font-family: Outfit, sans-serif;
    }

    .mv-summary-card,
    .ticket-preview-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-size: 13px;
        min-width: 300px;
        max-width: 360px;
    }

    .mv-summary-header,
    .ticket-preview-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .mv-error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .mv-header-title,
    .ticket-preview-header h4 {
        margin: 0;
        color: #fff;
        font-weight: 700;
        font-size: 14px;
    }

    .mv-header-subtitle,
    .ticket-preview-header p {
        margin: 2px 0 0;
        color: #fff;
        font-size: 11px;
        opacity: 0.82;
    }

    .mv-summary-body,
    .ticket-preview-body {
        padding: 14px 18px;
    }

    .mv-row,
    .mv-edit-row,
    .ticket-preview-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
        margin-bottom: 8px;
        box-sizing: border-box;
    }

    .mv-edit-row--top {
        align-items: flex-start;
    }

    .mv-label,
    .tp-label {
        width: 96px;
        min-width: 96px;
        color: #667085;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.4;
    }

    .mv-value,
    .tp-value {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
    }

    .mv-value--wrap,
    .tp-value {
        overflow-wrap: anywhere;
    }

    .mv-input,
    .mv-form-dropdown,
    .mv-multi {
        width: 190px;
        min-width: 190px;
        max-width: 190px;
    }

    .mv-input,
    .mv-textarea {
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 12px;
        box-sizing: border-box;
        outline: none;
    }

    .mv-input {
        height: 36px;
        padding: 7px 9px;
    }

        .mv-input:focus,
        .mv-textarea:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.1);
        }

    .mv-field {
        display: block;
        padding-top: 2px;
    }

        .mv-field .mv-label {
            display: block;
            width: auto;
            min-width: 0;
            margin-bottom: 5px;
        }

    .mv-textarea {
        width: 100%;
        min-height: 66px;
        padding: 8px 10px;
        resize: vertical;
    }

    .mv-actions {
        padding: 0 18px 16px;
        display: flex;
        gap: 8px;
    }

    .mv-actions--stacked {
        flex-direction: column;
    }

    .mv-btn {
        border-radius: 10px;
        padding: 10px;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

        .mv-btn:disabled,
        .mv-detail-btn:disabled,
        .mv-status-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .mv-btn--primary {
        border: 0;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        font-weight: 700;
    }

    .mv-btn--cancel {
        border: 0;
        background: none;
        color: #aaa;
        font-size: 12px;
        text-decoration: underline;
    }

    .ticket-preview-number {
        margin-left: auto;
        font-size: 11px;
        font-weight: 600;
    }

    .ticket-preview-error {
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

        .ticket-preview-error p {
            margin: 0;
        }

    .ticket-preview-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .mv-saved {
        color: #16a34a;
        font-weight: 600;
    }

    .mv-error-text {
        color: #dc2626;
        font-weight: 600;
    }

    .mv-text-message {
        width: fit-content;
        min-width: 0;
        max-width: 330px;
        padding: 12px 16px;
        border-radius: 6px 16px 16px 16px;
        background: #fff;
        color: #333;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
        box-sizing: border-box;
        overflow-wrap: break-word;
    }

    .mv-phone-card {
        width: 330px;
        max-width: 330px;
        padding: 0;
        border-radius: 16px;
        overflow: visible;
    }

    .mv-phone-question {
        width: 100%;
        padding: 13px 16px;
        margin: 0;
        box-sizing: border-box;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        border-radius: 16px 16px 0 0;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
    }

    .mv-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #fff;
    }

    .mv-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .mv-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

    .mv-phone-error {
        padding: 0 16px;
        margin: 0 0 5px;
        background: #fff;
        color: #dc2626;
        font-size: 11px;
    }

    .mv-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 4px 16px 16px;
    }

    .mv-preview-phone-row {
        width: 190px;
        min-width: 190px;
        max-width: 190px;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .mv-preview-country-dropdown {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        flex: 0 0 80px;
    }

    .mv-preview-phone-input {
        flex: 1;
        min-width: 0;
        width: auto;
    }

    :deep(.mv-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    :deep(.mv-preview-country-dropdown.p-dropdown) {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 36px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    :deep(.mv-form-dropdown.p-dropdown) {
        width: 190px;
        min-width: 190px;
        max-width: 190px;
        height: 36px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: none;
    }

    :deep(.mv-country-dropdown .p-dropdown-label),
    :deep(.mv-preview-country-dropdown .p-dropdown-label),
    :deep(.mv-form-dropdown .p-dropdown-label) {
        padding: 8px 7px;
        color: #1e2d45;
        font-size: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.mv-country-dropdown .p-dropdown-trigger),
    :deep(.mv-preview-country-dropdown .p-dropdown-trigger),
    :deep(.mv-form-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    /* Dropdown popup width fix only - functionality untouched */
    :deep(.mv-country-dropdown .p-dropdown-panel),
    :deep(.mv-preview-country-dropdown .p-dropdown-panel),
    :deep(.mv-form-dropdown .p-dropdown-panel) {
        width: 190px !important;
        min-width: 190px !important;
        max-width: 190px !important;
        z-index: 99999 !important;
    }

    :deep(.mv-country-dropdown .p-dropdown-items-wrapper),
    :deep(.mv-preview-country-dropdown .p-dropdown-items-wrapper),
    :deep(.mv-form-dropdown .p-dropdown-items-wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        max-height: 210px !important;
        overflow-x: hidden !important;
    }

    :deep(.mv-country-dropdown .p-dropdown-item),
    :deep(.mv-preview-country-dropdown .p-dropdown-item),
    :deep(.mv-form-dropdown .p-dropdown-item) {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;
        padding: 8px 10px !important;
        font-size: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .mv-multi {
        position: relative;
    }

    .mv-multi__trigger {
        width: 100%;
        min-height: 36px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        padding: 7px 9px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
    }

        .mv-multi__trigger > span:first-child {
            min-width: 0;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

    .mv-chevron {
        flex: 0 0 auto;
        color: #64748b;
        font-size: 10px;
    }

    .mv-multi__panel {
        position: absolute;
        top: calc(100% + 4px);
        right: 0;
        z-index: 1000;
        width: 220px;
        max-height: 190px;
        overflow-y: auto;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0 6px 18px rgba(15, 35, 65, 0.14);
    }

    .mv-check {
        min-height: 34px;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 10px;
        border-bottom: 1px solid #f1f5f9;
        color: #1e2d45;
        font-size: 12px;
        cursor: pointer;
        box-sizing: border-box;
    }

    .mv-check--all {
        font-weight: 700;
        background: #f8fafc;
    }

    .mv-check input {
        margin: 0;
        flex: 0 0 auto;
    }

    .mv-empty-inline {
        padding: 10px;
        color: #999;
        font-size: 11px;
        text-align: center;
    }

    .mv-error {
        margin: 8px 0 0;
        color: #dc2626;
        font-size: 11px;
    }

    .mv-list-body {
        padding: 0 16px 8px;
    }

    .mv-empty {
        padding: 16px 0;
        color: #999;
        font-size: 12px;
        text-align: center;
    }

    .mv-list-item {
        padding: 10px 0;
        border-bottom: 1px solid #e5e7eb;
    }

    .mv-list-item--last {
        border-bottom: 0;
    }

    .mv-list-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .mv-list-main {
        flex: 1;
        min-width: 0;
    }

    .mv-list-title {
        color: #1e2d45;
        font-size: 13px;
        font-weight: 600;
    }

    .mv-list-meta {
        margin-top: 3px;
        color: #64748b;
        font-size: 11px;
        overflow-wrap: anywhere;
    }

    .mv-list-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 5px;
        flex: 0 0 auto;
    }

    .mv-detail-btn,
    .mv-status-btn {
        border-radius: 12px;
        padding: 3px 9px;
        background: #fff;
        font-family: Outfit, sans-serif;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
    }

    .mv-detail-btn {
        border: 1px solid #cbd5e1;
        color: #1e2d45;
    }

    .mv-status-btn--active {
        border: 1px solid #86efac;
        color: #16803a;
        background: #f0fdf4;
    }

    .mv-status-btn--inactive {
        border: 1px solid #fca5a5;
        color: #dc2626;
        background: #fff7f7;
    }

    .mv-vendor-details {
        margin-top: 10px;
        padding: 10px 12px;
        border: 1px solid #e5e7eb;
        border-radius: 9px;
        background: #f8fafc;
    }

    .mv-detail-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding: 6px 0;
        border-bottom: 1px solid #e5e7eb;
        font-size: 11px;
    }

        .mv-detail-row:last-child {
            border-bottom: 0;
        }

        .mv-detail-row > span {
            min-width: 72px;
            color: #667085;
            font-weight: 600;
        }

        .mv-detail-row > strong {
            min-width: 0;
            color: #1e2d45;
            font-weight: 600;
            text-align: right;
            overflow-wrap: anywhere;
        }

    @media (max-width: 480px) {
        .mv-summary-card,
        .ticket-preview-card {
            min-width: 0;
            width: 100%;
            max-width: 330px;
        }

        .mv-text-message {
            max-width: 300px;
        }

        .mv-phone-card {
            width: 300px;
            max-width: 300px;
        }

        .mv-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.mv-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }

        .mv-label,
        .tp-label {
            width: 84px;
            min-width: 84px;
        }

        .mv-input,
        .mv-form-dropdown,
        .mv-multi,
        .mv-preview-phone-row {
            width: 175px;
            min-width: 175px;
            max-width: 175px;
        }

        :deep(.mv-form-dropdown.p-dropdown) {
            width: 175px;
            min-width: 175px;
            max-width: 175px;
        }

        .mv-preview-country-dropdown {
            width: 76px;
            min-width: 76px;
            max-width: 76px;
            flex-basis: 76px;
        }

        :deep(.mv-preview-country-dropdown.p-dropdown) {
            width: 76px;
            min-width: 76px;
            max-width: 76px;
        }

        .mv-multi__panel {
            width: 205px;
            max-width: calc(100vw - 50px);
        }
    }
</style>
